import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Search from '../common/components/Search';
import Facets from '../common/components/Facets';
import SearchResults from './components/SearchResults';
import { useSearchParams } from 'react-router-dom';
import { InvestorsDataService } from '../common/services';
import { InvestorResource } from '../common/entities';
import { Aggregations } from '../common/interfaces';
import Hero from '../common/components/Hero';

/**
 * @description High level component for dealing with Briefed.in investors list.
 * @return {JSX.Element}
 */
const Investors: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [investors, setInvestors] = useState<InvestorResource[]>([]);
  const [totalPages, setTotalPages] = useState<number>();
  const [investorAggregations, setInvestorAggregations] =
    useState<Aggregations>({});
  const [aggregationsOrder, setAggregationsOrder] = useState<string[]>([]);
  const [busy, setBusy] = useState<boolean>(false);

  const query = searchParams.get('q');
  const aggs = searchParams.get('a');
  const page = searchParams.get('p');

  const getInvestors = async (): Promise<void> => {
    setBusy(true);
    const result = await InvestorsDataService.getAllWithMeta(
      {
        query: query ? query : '',
        aggs: aggs ? aggs : '',
      },
      { number: page ? page : '1' },
    );
    setInvestors(result.data);
    if (
      result.meta !== undefined &&
      result.meta.aggregations !== undefined &&
      typeof result.meta.aggregations !== 'string' &&
      typeof result.meta.aggregations !== 'number' &&
      !Array.isArray(result.meta.aggregations)
    ) {
      setInvestorAggregations(result.meta.aggregations);
    }
    if (
      result.meta !== undefined &&
      result.meta.aggregationsOrder !== undefined &&
      Array.isArray(result.meta.aggregationsOrder)
    ) {
      setAggregationsOrder(result.meta.aggregationsOrder);
    }
    if (
      result.meta &&
      result.meta.pages &&
      typeof result.meta.pages === 'number'
    ) {
      setTotalPages(result.meta.pages);
    }
    setBusy(false);
  };

  // on first render
  useEffect(() => {
    getInvestors();
  }, []);

  // if query param changes
  useEffect(() => {
    getInvestors();
  }, [query, aggs, page]);

  return (
    <React.Fragment>
      <Hero title="Investors">
        We are the nation’s only public, independent system of record dedicated
        to Canadian technology startups and venture capital.
      </Hero>

      <Container className="marketplace">
        <Row className="justify-content-center mb-5">
          <Col>
            <Search query={query} />
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="text-muted p-0 text-xs">
              Looking for investor intros?
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={3} className="mt-3">
            <Facets
              searchQuery={query ? query : ''}
              filters={aggs ? aggs : ''}
              aggregations={investorAggregations}
              order={aggregationsOrder ? aggregationsOrder : []}
            />
          </Col>
          <Col md={9} className="mb-5">
            <SearchResults
              busy={busy}
              investors={investors}
              searchQuery={query ? query : ''}
              filters={aggs ? aggs : ''}
              page={page}
              totalPages={totalPages}
            />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default Investors;
